<script setup lang="ts">
import { PopoverColorEnum, PopoverPositionEnum, TirPmIconView, TirPmPopover } from 'tir-components';
import {
  computed,
  nextTick,
  onBeforeMount,
  onMounted,
  onUnmounted,
  PropType,
  ref,
  watch,
} from 'vue';
import {
  FilterOptions,
  GetGlobalStatusesItem,
  NamespaceModel,
  SelectedFilterOptions,
} from '../../../models';
import {
  ChartBarIcon,
  ChevronRightIcon,
  GlobeAsiaAustraliaIcon,
  ListBulletIcon,
  RectangleStackIcon,
  Squares2X2Icon,
  TagIcon,
} from 'tir-style-system/icons/outline';
import TirPmSearchInput from 'tir-pm-search-input/src/TirPmSearchInput.vue';
import { OptionFilterEnum } from '../enums';
import FilterParameter from './filter-parameter/FilterParameter.vue';
import { FilterOptionModalItem } from '../models/FilterOptionModalItem';
import { OptionItemModel } from '../models';
import FilterGlobalStatusesParameter from './filter-parameter/components/FilterGlobalStatusesParameter.vue';
import FilterProjectsParameter from './filter-parameter/components/FilterProjectsParameter.vue';
import { ProjectModel } from '@/models';
import { TagModel } from '@/components/task-forms/models';
import TaskParameter from '@/components/task-parameter/task-parameter.vue';
import FilterPointsParameter from './filter-parameter/components/FilterPointsParameter.vue';
import { TaskParameterTypeEnum } from '@/components/task-parameter';
import { DimondIcon, FlagIcon, StatusIcon } from 'tir-style-system/icons/solid';
import FilterNamespacesParameter from './filter-parameter/components/FilterNamespacesParameter.vue';
import { TypeModel } from '@/pages/namespace-types-settings-page/models';
import FilterTypeParameter from './filter-parameter/components/FilterTypeParameter.vue';
import {
  GetDifficultyListItemDetail,
  ProjectQueueTaskListItem,
} from '@/components/tasks-kanban/adapter';
import FilterDifficultysParameter from './filter-parameter/components/FilterDifficultysParameter.vue';
import FilterQueuesParameter from './filter-parameter/components/FilterQueuesParameter.vue';
import FilterStatusesParameter from './filter-parameter/components/FilterStatusesParameter.vue';

/**Параметры */
const props = defineProps({
  /**Поле поиска */
  searchField: {
    type: String,
  },
  /**Опции фильтра */
  filterOptions: {
    type: Object as PropType<FilterOptions>,
    required: true,
  },
  /**Опция */
  option: {
    type: Object as PropType<OptionFilterEnum>,
    required: true,
  },
  /**Выбранные элементы фильтра */
  selectedOptionsFilter: {
    type: Object as PropType<SelectedFilterOptions>,
    required: true,
  },
  /**показать только где я админ для проектов точно, для областей, для очередей ...' */
  showOnlyCurrentIsAdmin: {
    type: Boolean,
    required: false,
    default: true,
  },
});
/**События */
const emit = defineEmits<{
  /**Переключение чекбокса элемента*/
  toggleItem: [item: OptionItemModel, type: OptionFilterEnum, isMultiSelect: boolean];
  /**Выбрать все элементы */
  selectAllElements: [type: OptionFilterEnum, isMultiSelect: boolean];
  /**Получить ширину блока выбранных элементов */
  getWidthBlockSelectedElem: [count: number];
  /**Поиск элементов фильтра */
  searchElements: [value: string, option: OptionFilterEnum];
  /**Прокрут скрола до конца */
  scrollFinished: [type: OptionFilterEnum];
  /**Получить проекты */
  getProjects: [data: OptionItemModel[]];
  /**Получить теги проекта */
  getTagsProject: [tags: TagModel[]];
  /**Получить НС проекта */
  getNamespacesProject: [namespaces: OptionItemModel[]];
  /**Получить тип НС */
  getTypesNamespace: [types: TypeModel[]];
  /**Получить сложности */
  getDifficultys: [difficulty: OptionItemModel[]];
  /**Получить очереди */
  getQueues: [queue: ProjectQueueTaskListItem[]];
  /**Получить статусы */
  getStatuses: [statuses: OptionItemModel[], isGlobalStatusChanged?: boolean];
  /**Получить приоритеты */
  getPriorities: [priorities: OptionItemModel[]];
}>();
const $filterParameter = ref<InstanceType<typeof FilterParameter>>();
/**Ссылка на компонента поповера */
const $popover = ref<InstanceType<typeof TirPmPopover>>();
/**Ссылка на html элемент блока выбранных элементов */
const $selectedElemBlock = ref<HTMLElement | null>(null);
/** Ширина блока выбранных элементов */
const widthBlockSelectedElem = ref<number>(0);
/**Данная поля поиска */
const currentFieldValue = ref('');
/**Ссылка на поле поиска */
const $inputSearch = ref<InstanceType<typeof TirPmSearchInput>>();

/**Выбраны ли все элементы пункта фильтра */
const isFilterAllSelected = computed(() =>
  filterOptionData.value.isMultiSelect
    ? props.selectedOptionsFilter[props.option]?.length +
        (filterOptionData.value.HiddenElements[props.option]?.length || 0) ===
      props.filterOptions[props.option]?.length
    : props.selectedOptionsFilter[props.option]?.some((elem) => elem.Id === 0),
);
/**id выбранного проекта */
const idSelectedProject = computed(() =>
  Number(props.selectedOptionsFilter.Projects.map((item) => item.Id)),
);
/**id выбранного НС */
const idSelectedNamespace = computed(() =>
  String(props.selectedOptionsFilter.Namespaces.map((item) => item.Id)),
);
/**id выбранной очереди */
const idSelectedQueue = computed(() =>
  String(props.selectedOptionsFilter.Queues.map((item) => item.Id)),
);
/**Пуст ли массив выбранных элементов */
const isSelectionEmpty = computed(() => !props.selectedOptionsFilter[props.option]?.length);
/**Поле поиска */
const searchField = computed({
  get: () => {
    return currentFieldValue.value;
  },
  set: (newValue: string) => {
    currentFieldValue.value = newValue;
    emit('searchElements', newValue, props.option);
  },
});
onUnmounted(() => {
  $inputSearch.value?.value;
});
/**Отображение поповреа */
const isVisiblePopover = computed(() => $popover.value?.visible);
/**Данные опции фильтра */
const filterOptionData = computed<FilterOptionModalItem>(() => {
  switch (props.option) {
    case OptionFilterEnum.GlobalStatuses:
      return {
        Title: 'Глобальный статус',
        Icon: GlobeAsiaAustraliaIcon,
        LabelInputSearch: 'Поиск по статусам',
        Data: props.filterOptions.GlobalStatuses as OptionItemModel[],
        SelectedOptions: props.selectedOptionsFilter.GlobalStatuses,
        isMultiSelect: true,
        HiddenElements: props.selectedOptionsFilter.HiddenElements,
      };

    case OptionFilterEnum.Projects:
      return {
        Title: 'Проект',
        Icon: Squares2X2Icon,
        LabelInputSearch: 'Поиск по проектам',
        Data: props.filterOptions.Projects,
        isMultiSelect: false,
        SelectedOptions: props.selectedOptionsFilter.Projects,
        HiddenElements: props.selectedOptionsFilter.HiddenElements,
      };
    case OptionFilterEnum.Tags:
      return {
        Title: 'Теги',
        Icon: TagIcon,
        LabelInputSearch: 'Поиск по тегам',
        Data: props.filterOptions.Tags,
        SelectedOptions: props.selectedOptionsFilter.Tags,
        isMultiSelect: true,
        HiddenElements: props.selectedOptionsFilter.HiddenElements,
      };
    case OptionFilterEnum.Namespaces:
      return {
        Title: 'Рабочая область',
        Icon: DimondIcon,
        LabelInputSearch: 'Поиск по рабочим облостям',
        Data: props.filterOptions.Namespaces,
        SelectedOptions: props.selectedOptionsFilter.Namespaces,
        isMultiSelect: false,
        HiddenElements: props.selectedOptionsFilter.HiddenElements,
      };
    case OptionFilterEnum.Types:
      return {
        Title: 'Тип',
        Icon: RectangleStackIcon,
        LabelInputSearch: 'Поиск по типам',
        Data: props.filterOptions.Types,
        SelectedOptions: props.selectedOptionsFilter.Types,
        isMultiSelect: true,
        HiddenElements: props.selectedOptionsFilter.HiddenElements,
      };
    case OptionFilterEnum.Difficultys:
      return {
        Title: 'Сложность',
        Icon: ChartBarIcon,
        LabelInputSearch: 'Поиск по сложности',
        Data: props.filterOptions.Difficultys,
        SelectedOptions: props.selectedOptionsFilter.Difficultys,
        isMultiSelect: true,
        HiddenElements: props.selectedOptionsFilter.HiddenElements,
      };
    case OptionFilterEnum.Queues:
      return {
        Title: 'Очередь',
        Icon: ListBulletIcon,
        LabelInputSearch: 'Поиск по очередям',
        Data: props.filterOptions.Queues,
        SelectedOptions: props.selectedOptionsFilter.Queues,
        isMultiSelect: false,
        HiddenElements: props.selectedOptionsFilter.HiddenElements,
      };
    case OptionFilterEnum.Statuses:
      return {
        Title: 'Статус',
        Icon: StatusIcon,
        LabelInputSearch: 'Поиск по статусам',
        Data: props.filterOptions.Statuses,
        SelectedOptions: props.selectedOptionsFilter.Statuses,
        isMultiSelect: true,
        HiddenElements: props.selectedOptionsFilter.HiddenElements,
      };
    case OptionFilterEnum.Priorities:
      return {
        Title: 'Приоритет',
        Icon: FlagIcon,
        LabelInputSearch: 'Поиск по приоритетам',
        Data: props.filterOptions.Priorities,
        SelectedOptions: props.selectedOptionsFilter.Priorities,
        isMultiSelect: true,
        HiddenElements: props.selectedOptionsFilter.HiddenElements,
      };
  }
});
/**Отображать компонент в зависимости от опции */
const showComponent = computed(() => {
  switch (props.option) {
    case OptionFilterEnum.GlobalStatuses:
      return FilterGlobalStatusesParameter;
    case OptionFilterEnum.Projects:
      return FilterProjectsParameter;
    case OptionFilterEnum.Tags:
      return FilterPointsParameter;
    case OptionFilterEnum.Namespaces:
      return FilterNamespacesParameter;
    case OptionFilterEnum.Types:
      return FilterTypeParameter;
    case OptionFilterEnum.Difficultys:
      return FilterDifficultysParameter;
    case OptionFilterEnum.Queues:
      return FilterQueuesParameter;
    case OptionFilterEnum.Statuses:
      return FilterPointsParameter;
    case OptionFilterEnum.Priorities:
      return FilterPointsParameter;
  }
});
/**Тип для параметра */
const typeParameter = computed(() => {
  switch (props.option) {
    case OptionFilterEnum.Tags:
      return TaskParameterTypeEnum.Tag;
    case OptionFilterEnum.Statuses:
      return TaskParameterTypeEnum.Status;
    case OptionFilterEnum.Priorities:
      return TaskParameterTypeEnum.Priority;
  }
});
/**Проверка на наличие элементов */
const isDisabled = computed(
  () =>
    !searchField.value &&
    ((props.option === OptionFilterEnum.Priorities && !filterOptionData.value.Data?.length) ||
      (props.option === OptionFilterEnum.Projects && props.filterOptions.Projects?.length === 0)),
);
/**Определяет, должен ли отображаться плейсхолдер по умолчанию  */
const isDefaultPlaceholderVisible = computed(
  () => props.option == OptionFilterEnum.Namespaces || props.option == OptionFilterEnum.Queues,
);
// Вычисляемое свойство, которое возвращает нужный текст при выборе всех элементов
const filterTextSelectedAllElem = computed(() => {
  if (isDisabled.value) return '';

  // if (isFilterAllSelected.value && !isDefaultPlaceholderVisible.value) {
  //   return 'Все';
  // } else {
  //   return 'Не выбрано';
  // }
  return 'Все';
});
/**Выбранные элементы */
const selectedElements = computed(() => {
  const items = filterOptionData.value.SelectedOptions;
  let totalWidth = 0;

  return items.filter((item) => {
    totalWidth += item.Width || 0;
    return totalWidth <= 170;
  });
});
/**Скрытые элементы */
const hiddenElements = computed(() => {
  const items = filterOptionData.value.SelectedOptions;
  let totalWidth = 0;

  const cutoffIndex = items.findIndex((item) => {
    totalWidth += item.Width || 0;
    return totalWidth > 170;
  });

  return cutoffIndex !== -1 ? items.slice(cutoffIndex) : [];
});
/**Переключение чекбокса элемента*/
const toggleItem = async (item: OptionItemModel, type: OptionFilterEnum) => {
  await nextTick();
  emit('toggleItem', item, type, filterOptionData.value.isMultiSelect);
};
/**Переключение чекбокса выбора всех элементов*/
const selectAllElements = (type: OptionFilterEnum) => {
  emit('selectAllElements', type, filterOptionData.value.isMultiSelect);
  // $popover.value?.close();
};
/**Получить статусы */
const getStatuses = (statuses: GetGlobalStatusesItem[], isGlobalStatusChanged: boolean) => {
  emit('getStatuses', statuses, isGlobalStatusChanged);
};

// watch(
//   () => $popover.value?.visible,
//   async () => {
//     console.log('selectedOptionsFilter', props.selectedOptionsFilter);
//     await setTimeout(() => {
//       if (props.option === OptionFilterEnum.Projects) {
//         console.log('selectedOptionsFilter', props.selectedOptionsFilter);
//         $filterParameter?.value?.toggleItem(
//           props.selectedOptionsFilter.Projects[0],
//           OptionFilterEnum.Projects,
//           false,
//         );
//       }
//     }, 100);
//   },
// );
</script>
<template>
  <TirPmPopover
    ref="$popover"
    width="17.5rem"
    isNoTeleport
    disableContentBlur
    :isDisabled="isDisabled"
    :isHideTriangle="false"
    :position="PopoverPositionEnum.Right"
  >
    <div
      class="option-item-filter__block"
      :class="{ 'option-item-filter__active': isVisiblePopover }"
    >
      <div class="option-item-filter__content">
        <component
          :is="filterOptionData?.Icon"
          class="option-item-filter__icon"
          :class="{ 'option-item-filter__disabled-style': isDisabled }"
        />
        <div
          class="option-item-filter__selected-elements-block"
          :class="{ 'option-item-filter__disabled-style': isDisabled }"
        >
          {{ filterOptionData?.Title }}:
          <div v-if="isFilterAllSelected" class="option-item-filter__all-selected-elements-text">
            {{ filterTextSelectedAllElem }}
          </div>
          <div v-else class="option-item-filter__selected-elements" ref="$selectedElemBlock">
            <component
              v-for="item in selectedElements"
              isShortInfo
              :is="showComponent"
              :type="typeParameter"
              :key="item.Id"
              :item="item"
            />
          </div>
          <div
            v-if="isSelectionEmpty && !isFilterAllSelected && !isDisabled"
            class="option-item-filter__all-selected-elements-text"
          >
            <!-- {{ filterOptionData }} -->
            <span
              v-if="
                option == OptionFilterEnum.Projects ||
                option == OptionFilterEnum.Namespaces ||
                option == OptionFilterEnum.Queues
              "
              >Все</span
            >
            <span v-else>Не выбрано</span>
          </div>
          <div
            v-if="props.option === OptionFilterEnum.Priorities && isDisabled"
            class="option-item-filter__all-selected-elements-text"
            :class="{ 'option-item-filter__disabled-style': isDisabled }"
          >
            Не найдены
          </div>
          <TirPmPopover
            v-if="hiddenElements.length && !isFilterAllSelected"
            isTooltip
            :position="PopoverPositionEnum.Top"
          >
            <TirPmIconView :size="24" isRounded :isBordered="false" @click.stop>
              <template #mainSlot> +{{ hiddenElements.length }} </template>
            </TirPmIconView>
            <template #content>
              <div class="option-item-filter__popover-content option-item-filter__hidden-elements">
                <component
                  v-for="item in hiddenElements"
                  :is="showComponent"
                  :type="typeParameter"
                  :key="item.Id"
                  :item="item"
                />
              </div>
            </template>
            <div></div>
          </TirPmPopover>
        </div>
      </div>
      <ChevronRightIcon v-if="!isDisabled" class="option-item-filter__icon" />
    </div>
    <template #content>
      <div class="option-item-filter__popover-content">
        <TirPmSearchInput
          v-if="option !== OptionFilterEnum.GlobalStatuses"
          ref="$inputSearch"
          class="option-item-filter__input-search"
          placeholder=""
          isSmall
          v-model="searchField"
          :isWithPopup="false"
          :label="filterOptionData?.LabelInputSearch"
        />
        <FilterParameter
          ref="$filterParameter"
          :searchField="searchField"
          :selectedGlobalStatuses="props.selectedOptionsFilter.GlobalStatuses"
          :showComponent="showComponent"
          :typeParameter="typeParameter"
          :option="option"
          :filterOptionData="filterOptionData"
          :optionDataItem="filterOptionData?.Data"
          :selectedOptionsFilter="selectedOptionsFilter"
          :idSelectedProject="idSelectedProject"
          :idSelectedNamespace="idSelectedNamespace"
          :idSelectedQueue="idSelectedQueue"
          :showOnlyCurrentIsAdmin="showOnlyCurrentIsAdmin"
          @scrollFinished="emit('scrollFinished', $event)"
          @getTagsProject="emit('getTagsProject', $event)"
          @getQueues="emit('getQueues', $event)"
          @getStatuses="getStatuses"
          @getNamespacesProject="emit('getNamespacesProject', $event)"
          @getProjects="emit('getProjects', $event)"
          @getTypesNamespace="emit('getTypesNamespace', $event)"
          @getDifficultys="emit('getDifficultys', $event)"
          @getPriorities="emit('getPriorities', $event)"
          @toggleItem="toggleItem"
          @selectAllElements="selectAllElements"
        />
      </div>
    </template>
  </TirPmPopover>
</template>
<style scoped lang="scss">
.option-item-filter {
  &__disabled-style {
    color: var(--text-tertiary, #93969b);
  }
  &__active {
    background: var(--neutral-translucent-10, #272d371a);
  }
  &__all-selected-elements-text {
    font-weight: 500;
    font-size: 1rem;
  }
  &__input-search {
    white-space: nowrap;
    min-width: 100%;
  }
  &__hidden-elements > div {
    display: flex;
    align-items: center;
    padding: 0.75rem;
  }
  &__block {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    padding: 0.75rem;
    cursor: pointer;
    &:hover {
      background: var(--neutral-translucent-10, #272d371a);
    }
  }
  &__selected-elements-block {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  &__selected-elements {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  &__content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  &__icon {
    width: 1.5rem;
    height: 1.5rem;
  }
  &__popover-content {
    padding: 0.75rem;
    cursor: default;
  }
}
</style>
<style lang="scss">
.option-item-filter__input-search
  .tir-pm-search-input__input-container
  .input
  .input-container
  .input-container__input-wrapper
  .input-container__input-label {
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}
</style>
